import { useEffect, useState } from 'react'

/**
 * Hook utilitario para persistir un estado (serializable a JSON) en localStorage.
 *
 * Se usa para:
 * - cola offline de mutaciones (POS y Mesero)
 * - estados livianos que deben sobrevivir recargas
 *
 * Nota: no atrapa ni resuelve conflictos entre pestañas; sólo persiste el último valor escrito.
 */
export const useLocalStorageJsonState = <T>(key: string, initialValue: T) => {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return initialValue
    try {
      const raw = window.localStorage.getItem(key)
      if (!raw) return initialValue
      return JSON.parse(raw) as T
    } catch {
      return initialValue
    }
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(key, JSON.stringify(value))
    } catch {
      return
    }
  }, [key, value])

  return [value, setValue] as const
}

/**
 * Merge/dedupe de una cola offline de mutaciones.
 *
 * Objetivo: evitar que el usuario genere cientos de mutaciones cuando está sin conexión,
 * consolidando “lo último” por entidad.
 *
 * Estrategia actual:
 * - updateTable: merge por tableId
 * - updateOrder: merge por orderId
 * - updateProduct: merge por productId
 *
 * Si no hay match, se agrega la mutación al final.
 */
export const mergeOfflineQueueMutation = <T extends { kind: string; createdAt: string }>(prev: T[], mutation: T) => {
  const m = mutation as unknown as Record<string, unknown>

  if (m.kind === 'updateTable' && typeof m.tableId === 'string') {
    const idx = prev.findIndex((x) => (x as unknown as Record<string, unknown>).kind === 'updateTable' && (x as unknown as Record<string, unknown>).tableId === m.tableId)
    if (idx >= 0) {
      const existing = prev[idx] as unknown as Record<string, unknown>
      const mergedPayload =
        typeof existing.payload === 'object' && existing.payload !== null && typeof m.payload === 'object' && m.payload !== null
          ? { ...(existing.payload as Record<string, unknown>), ...(m.payload as Record<string, unknown>) }
          : (m.payload as unknown)

      const merged = { ...(existing as Record<string, unknown>), payload: mergedPayload, createdAt: m.createdAt } as unknown as T
      const next = [...prev]
      next[idx] = merged
      return next
    }
  }

  if (m.kind === 'updateOrder' && typeof m.orderId === 'string') {
    const idx = prev.findIndex((x) => (x as unknown as Record<string, unknown>).kind === 'updateOrder' && (x as unknown as Record<string, unknown>).orderId === m.orderId)
    if (idx >= 0) {
      const existing = prev[idx] as unknown as Record<string, unknown>
      const mergedPayload =
        typeof existing.payload === 'object' && existing.payload !== null && typeof m.payload === 'object' && m.payload !== null
          ? { ...(existing.payload as Record<string, unknown>), ...(m.payload as Record<string, unknown>) }
          : (m.payload as unknown)

      const merged = { ...(existing as Record<string, unknown>), payload: mergedPayload, createdAt: m.createdAt } as unknown as T
      const next = [...prev]
      next[idx] = merged
      return next
    }
  }

  if (m.kind === 'updateProduct' && typeof m.productId === 'string') {
    const idx = prev.findIndex(
      (x) => (x as unknown as Record<string, unknown>).kind === 'updateProduct' && (x as unknown as Record<string, unknown>).productId === m.productId
    )
    if (idx >= 0) {
      const existing = prev[idx] as unknown as Record<string, unknown>
      const mergedPayload =
        typeof existing.payload === 'object' && existing.payload !== null && typeof m.payload === 'object' && m.payload !== null
          ? { ...(existing.payload as Record<string, unknown>), ...(m.payload as Record<string, unknown>) }
          : (m.payload as unknown)

      const merged = { ...(existing as Record<string, unknown>), payload: mergedPayload, createdAt: m.createdAt } as unknown as T
      const next = [...prev]
      next[idx] = merged
      return next
    }
  }

  return [...prev, mutation]
}
